import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Circle, CircleDot, UserCheck } from 'lucide-react';
import SEO from '../components/SEO';
import { PageContainer } from '../components/PageHeader';
import { useStudentCourseDetail } from '../features/parentPortal/hooks/useStudentCourseDetail';
import ProgressBar from '../features/parentPortal/components/ProgressBar';
import Pill, { difficultyLabel, lessonStatusLabel, lessonStatusTone } from '../features/parentPortal/components/Pill';
import { Skeleton, SkeletonCircle, SkeletonRegion } from '../components/ui/Skeleton';
import ErrorBlock from '../features/parentPortal/components/ErrorBlock';
import { formatDate, relativeTime } from '../features/parentPortal/utils/format';
import '../styles/parentPortal.css';

export default function StudentCourseDetailPage() {
  const { studentId, courseId } = useParams();
  const { course, loading, error, reload } = useStudentCourseDetail(studentId, courseId);

  return (
    <>
      <SEO
        title={course?.title || 'Course'}
        path={`/family/students/${studentId}/courses/${courseId}`}
        noindex
      />
      <PageContainer>
        <Link
          to={`/family/students/${encodeURIComponent(studentId)}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: 13,
            fontWeight: 600,
            marginBottom: 'var(--space-4)',
          }}
        >
          <ArrowLeft size={14} strokeWidth={1.5} />
          Back to student
        </Link>

        {loading ? (
          <SkeletonRegion label="Loading course">
            <Skeleton width="54%" height={24} style={{ marginBottom: 'var(--space-3)' }} />
            <Skeleton height={6} style={{ marginBottom: 'var(--space-2)' }} />
            <Skeleton width="30%" height={12} style={{ marginBottom: 'var(--space-6)' }} />
            {Array.from({ length: 6 }, (_, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) 0' }}>
                <SkeletonCircle size={16} />
                <Skeleton height={13} style={{ flex: 1, maxWidth: `${76 - i * 4}%` }} />
              </div>
            ))}
          </SkeletonRegion>
        ) : error ? (
          <ErrorBlock message={error} onRetry={reload} />
        ) : !course ? null : (
          <>
            <CourseHeader course={course} />

            <section style={{ marginTop: 'var(--space-6)' }}>
              <h2
                style={{
                  margin: '0 0 var(--space-3)',
                  fontSize: 'var(--text-section-size)',
                  fontWeight: 'var(--text-section-weight)',
                  letterSpacing: 'var(--text-section-tracking)',
                  color: 'var(--text-primary)',
                }}
              >
                Lessons
              </h2>
              <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}>
                {(course.lessons || [])
                  .slice()
                  .sort((a, b) => (a.order || 0) - (b.order || 0))
                  .map((lesson) => (
                    <LessonRow key={lesson.lessonId} lesson={lesson} />
                  ))}
              </ol>
            </section>
          </>
        )}
      </PageContainer>
    </>
  );
}

function CourseHeader({ course }) {
  const completed = course.completedLessonCount ?? 0;
  const total = course.lessonCount ?? 0;
  const pct = course.progressPercent ?? 0;
  return (
    <div
      className="pp-card"
      style={{
        padding: 'var(--space-5)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)', flexWrap: 'wrap' }}>
        <Pill>{difficultyLabel(course.difficulty)}</Pill>
        {course.assignedByParent && (
          // Neutral, not green — a category label, not progress (spec rule 1).
          <Pill tone="neutral">
            <UserCheck size={11} strokeWidth={1.5} />
            Parent-assigned
          </Pill>
        )}
      </div>
      <h1
        style={{
          margin: '0 0 var(--space-4)',
          fontSize: 'var(--text-page-size)',
          fontWeight: 'var(--text-page-weight)',
          color: 'var(--text-primary)',
          letterSpacing: 'var(--text-page-tracking)',
        }}
      >
        {course.title}
      </h1>

      <ProgressBar percent={pct} height={8} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-3)',
          fontSize: 'var(--text-body-size)',
          color: 'var(--text-secondary)',
        }}
      >
        <span>
          {completed} of {total} lessons completed
        </span>
        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{pct}%</span>
      </div>
    </div>
  );
}

function LessonRow({ lesson }) {
  const tone = lessonStatusTone(lesson.status);
  const Icon = lesson.status === 'COMPLETED'
    ? CheckCircle2
    : lesson.status === 'IN_PROGRESS'
    ? CircleDot
    : Circle;
  const iconColor =
    lesson.status === 'COMPLETED'
      ? 'var(--success)'
      : lesson.status === 'IN_PROGRESS'
      ? 'var(--accent-primary)'
      : 'var(--text-tertiary)';

  let timeText = null;
  if (lesson.status === 'COMPLETED' && lesson.completedAt) {
    timeText = `Completed ${formatDate(lesson.completedAt)}`;
  } else if (lesson.status === 'IN_PROGRESS' && lesson.lastViewedAt) {
    timeText = `Last viewed ${relativeTime(lesson.lastViewedAt)}`;
  }

  return (
    <li
      className="pp-card-row"
      style={{
        padding: 'var(--space-3) var(--space-4)',
        display: 'flex',
        gap: 'var(--space-4)',
        alignItems: 'center',
      }}
    >
      <Icon size={22} style={{ color: iconColor, flexShrink: 0 }} strokeWidth={1.5} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: timeText ? 'var(--space-1)' : 0,
          }}
        >
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: 'var(--text-tertiary)',
              letterSpacing: '0.04em',
            }}
          >
            {String(lesson.order || '').padStart(2, '0')}
          </span>
          <span
            style={{
              fontSize: 'var(--text-panel-size)',
              fontWeight: 600,
              color: 'var(--text-primary)',
              minWidth: 0,
            }}
          >
            {lesson.title}
          </span>
        </div>
        {timeText && (
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{timeText}</div>
        )}
      </div>
      <Pill tone={tone}>{lessonStatusLabel(lesson.status)}</Pill>
    </li>
  );
}
